// src/components/dashboard/EnquiriesStatistics.jsx
import { useMemo, useState } from 'react';
import { Card, CardContent, Box,  TextField, Avatar, Grid, Button, CircularProgress } from '@mui/material';
import { Description } from '@mui/icons-material';
import axios from 'axios';
import { API_GET_EXPORT_ENQUIRIES, API_GET_EXPORT_INVOICES, API_GET_EXPORT_VISITORS } from '../../Util/URLConstants';
import { downloadBlob } from '../../Util/ApiHelper';
import SubscriptionDialog from '../../Util/SubscriptionDialog';
import MUITypography from '../../Components/MUITypography';
import { align_column, VM_Colors, vmPageTitleSx } from '../../Styles/VMStyles';
import StorageFactory from '../../Util/StorageFactory';
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_ROLES_PERMISSIONS } from '../../Util/VMConstants';
import { checkIsPermissionExist } from '../../Util/UtilHelper';


const EnquiriesStatistics = (props) => {
  const { defaultFrom, defaultTo, userID, canAccessPremiumFeatures } = props;
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);



  const sessionFactory = useMemo(
    () => new StorageFactory("session"),
    []
  );

  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const isDownloadRecordPermission = checkIsPermissionExist(
    VM_PERMISSIONS,
    "VM051"
  );

  const exportEnquiries = async () => {
    if(isDownloadRecordPermission === true){
    if (!canAccessPremiumFeatures) {
      setOpenSubscribeDialog(true);
      return;
    }
    setExportLoading('enquiries');
    try {
      const res = await axios.get(API_GET_EXPORT_ENQUIRIES, {
        params: { from: fromDate || undefined, to: toDate || undefined ,user_id : userID },
        responseType: 'blob'
      });
      downloadBlob(res.data, `enquiries_${Date.now()}.xlsx`);
    } catch (err) {
      console.error("Export enquiries error:", err);
    } finally {
      setExportLoading(null);
    }
    }else{
      alert(ACCESS_RESTRICTED_MESSAGE);
    }
  };
  
  const exportInvoices = async () => {
    if(isDownloadRecordPermission === true){
    if (!canAccessPremiumFeatures) {
      setOpenSubscribeDialog(true);
      return;
    }
    setExportLoading('invoices');
    try {
      const res = await axios.get(API_GET_EXPORT_INVOICES, {
        params: { from: fromDate || undefined, to: toDate || undefined ,user_id : userID },
        responseType: 'blob'
      });
      downloadBlob(res.data, `invoices_${Date.now()}.xlsx`);
    } catch (err) {
      console.error("Export Invoices error:", err);
    } finally {
      setExportLoading(null);
    }
    }else{
      alert(ACCESS_RESTRICTED_MESSAGE);
    }
  };
  const exportVisitors = async (loadingKey = 'visitors') => {
    if(isDownloadRecordPermission === true){
      if (!canAccessPremiumFeatures) {
        setOpenSubscribeDialog(true);
        return;
        }
        setExportLoading(loadingKey);
        try {
          const res = await axios.get(API_GET_EXPORT_VISITORS, {
            params: { from: fromDate || undefined, to: toDate || undefined ,user_id : userID },
            responseType: 'blob'
          });
          downloadBlob(res.data, `visitors_${Date.now()}.xlsx`);
        } catch (err) {
          console.error("Export visitors error:", err);
        } finally {
          setExportLoading(null);
        }
    }else{
      alert(ACCESS_RESTRICTED_MESSAGE);
    }
  };

  const canDownload = canAccessPremiumFeatures;

  const renderButtonLabel = (type, premiumLabel, upgradeLabel) =>
    exportLoading === type ? (
      <CircularProgress size={22} color="inherit" />
    ) : canDownload ? (
      premiumLabel
    ) : (
      upgradeLabel
    );

  return (
    <Card sx={{ boxShadow: 1 ,border:'1px solid #E0E0E0'}}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ ...align_column, alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <MUITypography variant="pageTitle" sx={{ ...vmPageTitleSx, mb: 1 }}> Downloads</MUITypography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
            <TextField label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
          </Box>
        </Box>

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={exportEnquiries}
                disabled={!canDownload || exportLoading === 'enquiries'}
              >
                {renderButtonLabel('enquiries', 'Download Enquiries', 'Upgrade to Download Enquiries')}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => exportVisitors('visitors')}
                disabled={!canDownload || exportLoading === 'visitors'}
              >
                {renderButtonLabel('visitors', 'Download Visitors', 'Upgrade to Download Visitors')}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (!canDownload) setOpenSubscribeDialog(true);
                  else exportInvoices();
                }}
                disabled={exportLoading === 'invoices'}
              >
                {renderButtonLabel('invoices', 'Download Invoices', 'Upgrade to Download Invoices')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
              <Description sx={{ width: 20, height: 20, color: VM_Colors.white }} />
            </Avatar>
            <MUITypography variant="h3" sx={{ fontWeight: 700 }}>
              Visitors Log
            </MUITypography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={() => exportVisitors('visitorsLog')}
            sx={{ fontSize: '16px', fontWeight: 400, mb: 1.5 }}
            disabled={!canDownload || exportLoading === 'visitorsLog'}
          >
            {exportLoading === 'visitorsLog' ? (
              <CircularProgress size={22} color="inherit" />
            ) : canDownload ? (
              'Download'
            ) : (
              'Upgrade to Download'
            )}
          </Button>
          <MUITypography variant="h3" color="text.secondary" align="center" sx={{ display: 'block' }}>
            Download the visitors log using from and to date.
          </MUITypography>
        </Box>
      </CardContent>

      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />
    </Card>
  );
};

export default EnquiriesStatistics;
